/**
 * One offline draft run awaiting review.
 *
 * Moved out of ReconciliationView, which held this card, the cached-order
 * conflict card, and the manual order queue card in a single 1,246-line file.
 *
 * See TCA-26081204.
 */
import React, { useState } from 'react';
import RunSendHoldNotice from './RunSendHoldNotice';
import { Loader2, RefreshCw, Route, User } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDeliveryDateDisplay } from '@/utils/dateUtils';
import AssignDriverDialog from '@/components/AssignDriverDialog';

const runReviewStateLabel = (state) => ({
  waiting_on_orders: 'Waiting on offline orders',
  ready_to_sync: 'Ready to sync',
  blocked: 'Blocked',
  replayed: 'Synced',
}[state] || 'Needs review');

const runReviewMessage = (state) => ({
  waiting_on_orders: 'Approve the offline orders in this run before syncing it.',
  ready_to_sync: 'All stops have cloud order IDs and this run can sync.',
  blocked: 'This run cannot sync until the listed stop issue is corrected. Use Review to remove invalid stops; adjust route composition later in Deliver.',
  replayed: 'This local run has already been synced to the cloud.',
}[state] || 'This offline run needs review.');

const OfflineRunCard = ({
  item,
  isAdmin,
  resolvingId,
  held = false,
  onRecheck,
  onRemoveStop,
  onSync,
  onDiscard,
  driverUsers = [],
  onAssignDriver,
  onUnassignDriver,
}) => {
  const run = item.run || {};
  // Compared against this run's own id, so only the card being acted on is
  // busy. The `:` form is a stop within this run.
  const busy = resolvingId === run.id || String(resolvingId || '').startsWith(`${run.id}:`);
  const ready = item.review_state === 'ready_to_sync';
  const blockers = item.blockers || [];
  const [showDriverDialog, setShowDriverDialog] = useState(false);
  const driver = run.driver || null;

  return (
    <fieldset disabled={held} className="min-w-0"><Card
      className={ready ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}
      data-testid={`offline-run-review-${run.id}`}
    >
      <CardHeader className="pb-3">
        <RunSendHoldNotice held={held} unqualified={run.run_send_eligible === false} />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Route className={`h-4 w-4 ${ready ? 'text-emerald-700' : 'text-amber-700'}`} />
              Offline run
              <span className="text-stone-900">{run.name || 'Offline Run'}</span>
            </CardTitle>
            <CardDescription>
              {run.date ? formatDeliveryDateDisplay(run.date) : 'No date'}
              {' · '}
              {item.stop_count || 0} stop{item.stop_count === 1 ? '' : 's'}
              {' · '}
              {runReviewStateLabel(item.review_state)}
            </CardDescription>
          </div>
          {isAdmin ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => onRecheck(item)}
                data-testid={`recheck-offline-run-${run.id}`}
              >
                {busy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Recheck run
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => onDiscard(item)}
                data-testid={`discard-offline-run-${run.id}`}
              >
                Delete local run
              </Button>
              <Button
                type="button"
                disabled={!ready || busy || run.run_send_eligible === false}
                onClick={() => onSync(item)}
                data-testid={`sync-offline-run-${run.id}`}
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Sync run to cloud
              </Button>
            </div>
          ) : (
            <div className="rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-stone-600">
              Admin action required
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <button
          type="button"
          disabled={!isAdmin || busy}
          onClick={() => setShowDriverDialog(true)}
          className="flex w-full items-center gap-2 rounded-md border border-orange-200 bg-white p-3 text-left disabled:cursor-not-allowed disabled:opacity-70"
          data-testid={`review-offline-run-driver-${run.id}`}
        >
          <User className="h-4 w-4 text-orange-700" />
          <div>
            <div className="font-medium text-stone-900">
              {driver ? driver.name : 'No driver proposed'}
            </div>
            <div className="text-xs text-orange-700">
              Offline proposed assignment — cloud/mobile confirmation pending
            </div>
            {driver?.eligible_cached === false ? (
              <div className="mt-1 text-xs font-medium text-red-700">{driver.eligibility_message}</div>
            ) : null}
          </div>
        </button>
        <div className="rounded-md border border-white bg-white/80 p-3 text-stone-700">
          {runReviewMessage(item.review_state)}
        </div>
        {run.last_replay_error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800">
            Last sync attempt: {run.last_replay_error}
          </div>
        )}
        {blockers.length > 0 && (
          <div className="space-y-2">
            {blockers.map((blocker) => (
              <div
                key={`${blocker.stop_id}-${blocker.state}`}
                className="rounded-md border border-amber-200 bg-white p-3"
              >
                <div className="font-medium text-stone-900">
                  Stop {blocker.sequence || '?'}
                  {blocker.order?.internal_order_number
                    ? ` · ${blocker.order.internal_order_number}`
                    : ''}
                </div>
                {blocker.order?.recipient_name ? (
                  <div className="text-stone-700">{blocker.order.recipient_name}</div>
                ) : null}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-stone-600">Issue: {blocker.message}</div>
                  {isAdmin && blocker.stop_id ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => onRemoveStop(item, blocker)}
                      data-testid={`remove-offline-run-stop-${run.id}-${blocker.stop_id}`}
                    >
                      {resolvingId === `${run.id}:${blocker.stop_id}` ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Remove stop
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <AssignDriverDialog
        open={showDriverDialog && !held}
        onOpenChange={setShowDriverDialog}
        driverUsers={driverUsers}
        currentUserId={run.user_id}
        onAssignUser={(userId) => onAssignDriver(item, userId)}
        onUnassignUser={() => onUnassignDriver(item)}
        runName={run.name}
        offlineProposal
      />
    </Card></fieldset>
  );
};

export default OfflineRunCard;
